import React from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Package, ShoppingBag, Users, TrendingUp, FileText, Mail, ShoppingCart, Clock, AlertTriangle, Bell } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';

const OverviewTab = ({ 
  stats, 
  productForm, 
  setProductForm, 
  showProductDialog, 
  setShowProductDialog,
  handleCreateProduct,
  setActiveSection 
}) => {
  return (
    <div className="animate-fade-in">
      <h1 className="text-3xl font-bold text-[#0a0203] mb-8">Dashboard</h1>
      
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {[
          { label: 'Total Revenue', value: `$${stats?.total_revenue?.toLocaleString() || 0}`, icon: DollarSign, color: 'bg-green-100 text-green-600' },
          { label: 'Products', value: stats?.products_count || stats?.total_products || 0, icon: Package, color: 'bg-blue-100 text-blue-600' },
          { label: 'Orders', value: stats?.orders_count || stats?.total_orders || 0, icon: ShoppingBag, color: 'bg-purple-100 text-purple-600' },
          { label: 'Leads', value: stats?.leads_count || stats?.total_leads || 0, icon: Users, color: 'bg-orange-100 text-orange-600' },
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-2xl p-6 shadow-sm" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                <stat.icon className="h-6 w-6" />
              </div>
              <TrendingUp className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-[#0a0203]" style={{ fontFamily: 'JetBrains Mono' }}>{stat.value}</p>
            <p className="text-gray-500 text-sm">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Cart & Abandoned Cart Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Units in Cart */}
        <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="stat-units-in-cart">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-indigo-100 text-indigo-600">
              <ShoppingCart className="h-6 w-6" />
            </div>
          </div>
          <p className="text-2xl font-bold text-[#0a0203]" style={{ fontFamily: 'JetBrains Mono' }}>
            {stats?.units_in_cart || 0}
          </p>
          <p className="text-gray-500 text-sm">Units in Carts</p>
          <p className="text-xs text-gray-400 mt-1">{stats?.active_carts || 0} active carts</p>
        </div>

        {/* Abandoned Cart Breakdown */}
        <div className="bg-white rounded-2xl p-6 shadow-sm col-span-1 md:col-span-2 lg:col-span-2" data-testid="stat-abandoned-carts">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-amber-100 text-amber-600">
              <Clock className="h-6 w-6" />
            </div>
            <span className="text-sm font-medium text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
              {stats?.abandoned_carts?.total || 0} total
            </span>
          </div>
          <p className="text-lg font-semibold text-[#0a0203] mb-3">Abandoned Carts</p>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-xl font-bold text-green-600" style={{ fontFamily: 'JetBrains Mono' }}>
                {stats?.abandoned_carts?.within_24h || 0}
              </p>
              <p className="text-xs text-gray-600">≤ 24 hours</p>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <p className="text-xl font-bold text-yellow-600" style={{ fontFamily: 'JetBrains Mono' }}>
                {stats?.abandoned_carts?.within_48h || 0}
              </p>
              <p className="text-xs text-gray-600">24-48 hours</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <p className="text-xl font-bold text-red-600" style={{ fontFamily: 'JetBrains Mono' }}>
                {stats?.abandoned_carts?.over_72h || 0}
              </p>
              <p className="text-xs text-gray-600">72+ hours</p>
            </div>
          </div>
        </div>

        {/* Stock Alerts */}
        <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="stat-stock-alerts">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-red-100 text-red-600">
              <AlertTriangle className="h-6 w-6" />
            </div>
            {stats?.pending_stock_alerts > 0 && (
              <Bell className="h-5 w-5 text-red-500 animate-pulse" />
            )}
          </div>
          <p className="text-2xl font-bold text-[#0a0203]" style={{ fontFamily: 'JetBrains Mono' }}>
            {stats?.low_stock_products || 0}
          </p>
          <p className="text-gray-500 text-sm">Low/Out of Stock</p>
          <p className="text-xs text-gray-400 mt-1">{stats?.pending_stock_alerts || 0} pending alerts</p>
        </div>
      </div>

      {/* Recent Leads & Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-[#0a0203] mb-4">Recent Leads</h3>
          <div className="space-y-4">
            {stats?.recent_leads?.slice(0, 5).map((lead, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div>
                  <p className="font-medium text-[#0a0203]">{lead.name}</p>
                  <p className="text-sm text-gray-500">{lead.email}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  lead.type === 'inquiry' ? 'bg-purple-100 text-purple-600' : 
                  lead.type === 'commercial' ? 'bg-green-100 text-green-600' :
                  'bg-blue-100 text-blue-600'
                }`}>
                  {lead.type}
                </span>
              </div>
            ))}
            {(!stats?.recent_leads || stats.recent_leads.length === 0) && (
              <p className="text-gray-500 text-center py-4">No leads yet</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-[#0a0203] mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-4">
            <Link to="/admin/pages/builder">
              <Button variant="outline" className="w-full h-24 flex-col gap-2 border-dashed" data-testid="quick-create-page">
                <FileText className="h-6 w-6" />
                <span>Create Page</span>
              </Button>
            </Link>
            <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full h-24 flex-col gap-2 border-dashed" data-testid="quick-add-product">
                  <Package className="h-6 w-6" />
                  <span>Add Product</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add New Product</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateProduct} className="space-y-4">
                  <Input placeholder="Product Name" value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} required />
                  <Input placeholder="Description" value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} required />
                  <Input type="number" placeholder="Price" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} required />
                  <Input placeholder="Image URL" value={productForm.image_url} onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })} required />
                  <Input placeholder="Features (comma-separated)" value={productForm.features} onChange={(e) => setProductForm({ ...productForm, features: e.target.value })} />
                  <Button type="submit" className="w-full bg-[#003456] hover:bg-[#001d30]">Create Product</Button>
                </form>
              </DialogContent>
            </Dialog>
            <Button variant="outline" className="w-full h-24 flex-col gap-2 border-dashed" onClick={() => setActiveSection('leads')}>
              <Mail className="h-6 w-6" />
              <span>View Leads</span>
            </Button>
            <Button variant="outline" className="w-full h-24 flex-col gap-2 border-dashed" onClick={() => setActiveSection('orders')}>
              <ShoppingBag className="h-6 w-6" />
              <span>View Orders</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
